import api, { ApiResponse } from './api';

export interface PacienteVfp {
  clave: number;
  nombre?: string;
  apellido1?: string;
  apellido2?: string;
  nacimiento?: Date;
  peso?: number;
  temp?: number;
  talla?: number;
  historial?: string;
  sexo?: string;
  ta?: number;
  procedencia?: string;
  medicoVfpPaciDat?: {
    clave: number;
    direccion?: string;
    tel1?: string;
    tel2?: string;
    padre?: string;
  };
}

export type ClasificacionValor = 'muy_bajo' | 'bajo' | 'normal_bajo' | 'normal' | 'alto' | 'muy_alto' | 'fuera_de_rango';

export interface PesoTallaReferencia {
  id: number;
  sexo?: string | null;
  anos?: number | null;
  meses?: number | null;
  pesoB: number | null;
  pesoP: number | null;
  pesoA: number | null;
  tallaB: number | null;
  tallaP: number | null;
  tallaA: number | null;
}

export interface PesoIdmReferencia {
  id: number;
  talla?: number | null;
  limInf?: number | null;
  limInfR?: number | null;
  pesoPr?: number | null;
  limSup?: number | null;
  limSupR?: number | null;
}

export interface PesoIdhReferencia {
  id: number;
  talla?: number | null;
  esqL1?: number | null;
  esqL2?: number | null;
  esqM1?: number | null;
  esqM2?: number | null;
  esqP1?: number | null;
  esqP2?: number | null;
}

export interface PesoTallaOMSReferencia {
  id: number;
  sexo: string;
  meses: number;
  anos: number;
  mesesRestantes: number;
  pesoP3: number | null;
  pesoP15: number | null;
  pesoP50: number | null;
  pesoP85: number | null;
  pesoP97: number | null;
  tallaP3: number | null;
  tallaP15: number | null;
  tallaP50: number | null;
  tallaP85: number | null;
  tallaP97: number | null;
}

export interface PesoTallaPacienteResumen {
  clave: number;
  sexo: string | null;
  nacimiento: string | Date | null;
  peso: number | null;
  talla: number | null;
}

export interface PesoTallaResponse {
  paciente: PesoTallaPacienteResumen;
  edadActual: { anos: number; meses: number } | null;
  parametrosBusqueda: { anos: number | null; meses: number | null };
  referencia: PesoTallaReferencia | null;
  comparacion: {
    sexoCoincide: boolean;
    peso: ClasificacionValor | null;
    talla: ClasificacionValor | null;
  } | null;
  missingFields: string[];
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface DoctorInfo {
  id: number;
  nombre?: string;
  especialid?: string;
  cedula1?: string;
  cedula2?: string;
  cabecera1?: string;
  cabecera2?: string;
  cabecera3?: string;
  cabecera4?: string;
  consultori?: string;
  consultor2?: string;
  consultor3?: string;
  domicilio1?: string;
  domicilio2?: string;
  domicilio3?: string;
  tipoR?: number;
}

class MedicoVfpService {
  // ==================== PACIENTES VFP ====================

  async getPacientesVfp(params?: {
    skip?: number;
    take?: number;
    searchNombre?: string;
    searchApellido1?: string;
    searchApellido2?: string;
    searchGeneral?: string;
    isActive?: boolean;
    procedencia?: string;
  }): Promise<PaginatedResponse<PacienteVfp>> {
    const response = await api.get<ApiResponse<PaginatedResponse<PacienteVfp>>>('medico-vfp/pacientes', {
      params,
    });
    return response.data.data;
  }

  async getPacienteVfp(clave: number): Promise<PacienteVfp> {
    const response = await api.get<ApiResponse<PacienteVfp>>(`medico-vfp/pacientes/${clave}`);
    return response.data.data;
  }

  async updatePacienteVfp(clave: number, data: Partial<Omit<PacienteVfp, 'nacimiento'>> & {
    nacimiento?: string;
    direccion?: string;
    tel1?: string;
    tel2?: string;
    padre?: string;
    procedencia?: string;
  }): Promise<PacienteVfp> {
    const response = await api.put<ApiResponse<PacienteVfp>>(`medico-vfp/pacientes/${clave}`, data);
    return response.data.data;
  }

  async createPacienteVfp(data: {
    nombre: string;
    apellido1: string;
    apellido2?: string;
    sexo: string;
    nacimiento?: string;
    peso?: number;
    talla?: number;
    temp?: number;
    ta?: number;
    direccion?: string;
    tel1?: string;
    tel2?: string;
    padre?: string;
    procedencia?: string;
  }): Promise<PacienteVfp> {
    const response = await api.post<ApiResponse<PacienteVfp>>('medico-vfp/pacientes', data);
    return response.data.data;
  }

  async deletePacienteVfp(clave: number): Promise<void> {
    await api.delete(`medico-vfp/pacientes/${clave}`);
  }

  async getPacientePesoTalla(
    clave: number,
    params?: { anos?: number; meses?: number },
  ): Promise<PesoTallaResponse> {
    const response = await api.get<ApiResponse<PesoTallaResponse>>(
      `medico-vfp/pacientes/${clave}/peso-talla`,
      {
        params,
      },
    );
    return response.data.data;
  }

  async getPesoTallaTabla(): Promise<PesoTallaReferencia[]> {
    const response = await api.get<ApiResponse<PesoTallaReferencia[]>>('medico-vfp/peso-talla/tabla');
    return response.data.data;
  }

  async getPesoIdmTabla(): Promise<PesoIdmReferencia[]> {
    const response = await api.get<ApiResponse<PesoIdmReferencia[]>>('medico-vfp/peso-idm/tabla');
    return response.data.data;
  }

  async getPesoIdhTabla(): Promise<PesoIdhReferencia[]> {
    const response = await api.get<ApiResponse<PesoIdhReferencia[]>>('medico-vfp/peso-idh/tabla');
    return response.data.data;
  }

  async getPesoTallaOMSTabla(sexo?: string): Promise<PesoTallaOMSReferencia[]> {
    const params = sexo ? { sexo } : {};
    const response = await api.get<ApiResponse<PesoTallaOMSReferencia[]>>('medico-vfp/peso-talla-oms/tabla', { params });
    return response.data.data;
  }

  async getDoctorInfo(): Promise<DoctorInfo> {
    const response = await api.get<ApiResponse<DoctorInfo>>('medico-vfp/doctor');
    return response.data.data;
  }

  async calcularPercentiles(params: {
    sexo: string;
    anos: number;
    meses: number;
    peso?: number;
    talla?: number;
  }): Promise<{ peso: ClasificacionValor | null; talla: ClasificacionValor | null }> {
    const response = await api.get<ApiResponse<{ peso: ClasificacionValor | null; talla: ClasificacionValor | null }>>(
      'medico-vfp/percentiles',
      { params },
    );
    return response.data.data;
  }
}

export default new MedicoVfpService();
